import MagazineComponent from '@pixum/magazine';
import ThemeProvider from '@pixum/theme-provider';
import { RichTextContent, type RichTextDocument } from '../../richtText';
import { MagazineActions, MagazinePicture } from './partials';
import { THEME_DEFAULT_PROPS } from 'data-layer/constants';
import type { MagazineProps } from '../types';
import { isRichTextEmpty } from '../../richtText/utils/isRichTextEmpty';
import { Video } from '../../video/Video';
import { CountdownWrapper } from '../../countdown';
import { getVideoAspectRatioStyle } from '../utils/getMediaAspectRatioStyle';
import '../mediaWrapper.css';

/**
 * MagazineVariant is a React functional component that renders a magazine layout.
 *
 * Props:
 * - props: An object of type MagazineNode containing data for the magazine.
 *
 * Features:
 * - Transforms rich text content using RichTextContent.
 * - Displays an image or video with optimized loading for LCP (Largest Contentful Paint).
 * - Uses MagazinePicture for responsive image optimization with pictureData.
 * - Applies a theme to the magazine using ThemeProvider, defaulting to DEFAULT_MAGAZINE_THEME if no theme is provided.
 * - Renders actions such as primary and secondary call-to-actions for both mobile and desktop views.
 * - Enables media click functionality when used as KeyVisual (redirects to primary CTA on image click).
 *
 * Dependencies:
 * - Magazine: Component for rendering the magazine layout.
 * - ThemeProvider: Provides theming capabilities.
 * - RichTextContent: Utility for rendering rich text content.
 * - MagazinePicture: Optimized responsive picture element for LCP (uses Image component as fallback).
 * - Video: Component for rendering videos (HTML5/YouTube).
 * - MagazineActions: Component for rendering call-to-action buttons.
 */
export function Magazine(props: MagazineProps) {
  const {
    links,
    image,
    video,
    pictureData,
    theme,
    constants,
    device,
    isUsedAsKeyVisual = false,
    enableMediaClick,
    level,
    countdown,
    ...magazineProps
  } = props;

  const text = props.text && !isRichTextEmpty(props.text as RichTextDocument) && <RichTextContent node={props.text} />;
  const actions =
    links && links.length > 0 ? <MagazineActions buttons={links} constants={constants} device={device} /> : undefined;

  // Pass clickUrl to MagazinePicture when media should be clickable
  const primaryLinkUrl = links?.[0]?.url;
  const isMediaClickable = enableMediaClick && primaryLinkUrl;

  // Render video if available, otherwise fall back to image
  // MagazinePicture uses pictureData for LCP optimization when available, falls back to Image component
  const media = video ? (
    <div className="magazine-media-wrapper magazine-media-wrapper--video" style={getVideoAspectRatioStyle()}>
      <Video video={video} />
    </div>
  ) : (
    image && (
      <MagazinePicture
        image={image}
        pictureData={pictureData}
        device={device}
        isUsedAsKeyVisual={isUsedAsKeyVisual}
        clickUrl={isMediaClickable ? (primaryLinkUrl as string) : undefined}
      />
    )
  );

  const magazineTheme = theme || THEME_DEFAULT_PROPS;
  const countdownElement = countdown ? <CountdownWrapper countdown={countdown} /> : undefined;

  return (
    <ThemeProvider theme={magazineTheme}>
      <MagazineComponent
        {...magazineProps}
        text={text}
        media={media}
        actions={actions}
        level={level}
        countdown={countdownElement}
        {...(theme ? { theme } : {})}
      />
    </ThemeProvider>
  );
}
